import { useEffect, useRef, useState } from "react"
import { useMsal } from '@azure/msal-react'
import { getAuthHeader, REACT_APP_PS_PORTAL_API_URI } from 'authHelper'
import { useParams } from 'react-router-dom'
import _ from 'lodash'
import apiFetch from 'apiFetch'
import { Toast, ToastContainer, Button, Col, Container, Row, Card } from "react-bootstrap"
import { Formik, Form, Field, ErrorMessage } from 'formik'
import BForm from 'react-bootstrap/Form'
import * as Yup from 'yup'
import './OnboardPracticeDetails.css'
import Progress from "./Progress"
import OnboardPracticeGroup from "./OnboardPracticeGroup"
import { ConfirmDialog } from "components/ConfirmDialog"

const OnboardPracticeDetails = () => {
    const [practice, setPractice] = useState({})
    const [showToastMsg, setShowToastMsg] = useState({ visible: false, msg: '' })
    const [refData, setRefData] = useState({})
    const [submit, setSubmit] = useState(0)
    const [showConfirmCloseWorkflow, setShowConfirmCloseWorkflow] = useState(false)
    const [showConfirmCancellation, setShowConfirmCancellation] = useState(false)
    const [showRestartOnboardingConfirm, setShowRestartOnboardingConfirm] = useState(false)
    const windowFocused = useRef(document.hasFocus())
    const intervalRef = useRef(null)
    const [showConfirmSaveDialog, setShowConfirmSaveDialog] = useState(false)
    const [startOnboarding, setStartOnboarding] = useState(false)
    const { instance, accounts } = useMsal()
    const [customDirty, setCustomDirty] = useState({
        practiceName: false,
        abbreviation: false,
        practiceSoftwareId: false,
        pipQIStatusID: false,
        postCode: false,
        practiceGroupId: false,
        phnName: false,
        managerEmail: false,
        itProviderEmail: false,
        onboardingCheck: false
    })

    const params = useParams()
    const STATUS_TO_BE_ONBOARDED = 10;
    const STATUS_CLIENT_ID_EMAILED = 30;
    const STATUS_INSTALLATION_STARTED = 40;
    const STATUS_COMPLETE = 50;
    const STATUS_CANCELLED = 100;

    const clearAutoStatusUpdate = () => {
    if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
        }
    }

    const shouldPoll = (practiceDetails) => {
        return document.visibilityState === 'visible'
            && windowFocused.current
            && practiceDetails.dataShareAgreementConfirmed === true
            && practiceDetails.onboardingStatusID >= STATUS_TO_BE_ONBOARDED
            && practiceDetails.onboardingStatusID < STATUS_COMPLETE
    }

    const mapToPracticeDetails = (data) => {
        return {
            id: data.id,
            practiceName: _.get(data, 'name'),
            abbreviation: _.get(data, 'abbreviation'),
            practiceSoftwareId: _.get(data, 'software.id'),
            postCode: data.postCode != null ? data.postCode.toString().padStart(4, '0') : '',
            practiceGroupId: _.get(data, 'group.id'),
            phnName: _.get(data, 'phn.name'),
            pipQIStatusID: _.get(data, 'pipqiStatus.id'),
            managerEmail: _.get(data, 'managerEmail'),
            itProviderEmail: _.get(data, 'itProviderEmail'),
            dataShareAgreementConfirmed: _.get(data, 'dataShareAgreementConfirmed'),
            onboardingStatusID: _.get(data, 'onboardingStatusID'),
            onboardingStatus: _.get(data, 'onboardingStatus.description'),
            appClientId: _.get(data, 'appClientId'),
            onboardingWorkflowHasError: _.get(data, 'onboardingWorkflowHasError'),
            onboardingWorkflowCreateTime: Date.parse(_.get(data, 'onboardingWorkflowCreateTime'))
        }
    }

    async function fetchData() {
        const authHeader = await getAuthHeader(instance, accounts)
        const httpConfig = {
            headers: authHeader
        }

        const result = await apiFetch.get(`${REACT_APP_PS_PORTAL_API_URI}/onboard/${params.practiceId}`, httpConfig)
        const refResult = await apiFetch.get(`${REACT_APP_PS_PORTAL_API_URI}/practices/refdata`, httpConfig)
        setRefData(refResult.data)
        const practiceDetails = mapToPracticeDetails(result.data)
        setPractice(practiceDetails)

        if (shouldPoll(practiceDetails)) {
            setupAutoStatusUpdate()
        } else {
            clearAutoStatusUpdate()
        }
    }

    useEffect(() => {
        fetchData()

        return () => {
            // Anything in here is fired on component unmount.
            clearAutoStatusUpdate()
        }
    }, [submit, params.practiceId])

    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                await fetchData()
            } else {
                clearAutoStatusUpdate()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [submit, params.practiceId])

    useEffect(() => {
        const handleWindowFocus = async () => {
            windowFocused.current = true
            await fetchData()
        }

        const handleWindowBlur = () => {
            windowFocused.current = false
            clearAutoStatusUpdate()
        }

        window.addEventListener('focus', handleWindowFocus)
        window.addEventListener('blur', handleWindowBlur)

        return () => {
            window.removeEventListener('focus', handleWindowFocus)
            window.removeEventListener('blur', handleWindowBlur)
        }
    }, [submit, params.practiceId])

    const validationSchema = (statusID, onboardingHasStarted) => Yup.object().shape({
        practiceName: Yup.string()
            .required('Name is required')
            // @ts-ignore
            .csvInputCheck('Name must NOT contain comma (,) or double quote (")')
            .max(255, 'Name must not exceed 255 characters'),
        abbreviation: Yup.string()
            .required('Abbreviation is required')
            .max(20, 'Abbreviation must not exceed 20 characters'),
        postCode: Yup.string()
            .required('Postcode is required')
            .matches(/^\d{3,4}$/, 'Invalid postcode'),
        practiceSoftwareId: Yup.string()
            .nullable()
            .required('Software is required'),
        pipQIStatusID: Yup.string()
            .nullable()
            .required('PIP QI Status is required'),
        managerEmail: Yup.string()
            .nullable()
            .email('Manager Email is not valid')
            .when('dataShareAgreementConfirmed', {
                is: (value) => value || statusID > STATUS_TO_BE_ONBOARDED,
                then: (schema) => schema.required('Manager Email is required'),
                otherwise: (schema) => schema.nullable()
            }),
        itProviderEmail: Yup.string()
            .nullable()
            .email('IT Provider Email is not valid')
            .when('dataShareAgreementConfirmed', {
                is: (value) => value || statusID > STATUS_TO_BE_ONBOARDED,
                then: (schema) => schema.required('IT Provider Email is required'),
                otherwise: (schema) => schema.nullable()
            }),
        dataShareAgreementConfirmed: Yup.boolean()
            .when([], {
                is: () => onboardingHasStarted,
                then: (schema) => schema.test('isDSAChecked', 'Please accept data sharing agreement', function (value) {
                    return value
                }),
            }),
    })

    const setupAutoStatusUpdate = () => {
        clearAutoStatusUpdate()
        console.log('setupAutoStatusUpdate')

        intervalRef.current = setInterval(async () => {
            if (document.visibilityState !== 'visible' || !windowFocused.current) {
                clearAutoStatusUpdate()
                return
            }

            const authHeader = await getAuthHeader(instance, accounts)
            const httpConfig = {
                headers: authHeader
            }

            const result = await apiFetch.get(`${REACT_APP_PS_PORTAL_API_URI}/onboard/${params.practiceId}`, httpConfig)
            const practiceDetails = mapToPracticeDetails(result.data)

            setPractice(practiceDetails)

            if (!shouldPoll(practiceDetails)) {
                clearAutoStatusUpdate()
            }
        }, 30000)
    }

    const fetchRef = async (newPracticeGroupId) => {
        const authHeader = await getAuthHeader(instance, accounts)
        const httpConfig = {
            headers: authHeader
        }
        const refResult = await apiFetch.get(`${REACT_APP_PS_PORTAL_API_URI}/practices/refdata`, httpConfig)

        setRefData(refResult.data)
        setPractice({
            ...practice,
            practiceGroupId: newPracticeGroupId
        })
    }

    const closeWorkflow = async (setIsSubmitting) => {
        setIsSubmitting(true)

        const authHeader = await getAuthHeader(instance, accounts)
        const httpConfig = {
            headers: authHeader
        }
        await apiFetch.post(`${REACT_APP_PS_PORTAL_API_URI}/onboard/${params.practiceId}/close`, null, httpConfig)
        setShowToastMsg({ visible: true, msg: 'Onboarding closed' })

        setIsSubmitting(false)
        setShowConfirmCloseWorkflow(false)
        //refresh
        setSubmit(count => count + 1)
    }

    const cancelWorkflow = async (setIsSubmitting) => {
        setIsSubmitting(true)

        const authHeader = await getAuthHeader(instance, accounts)
        const httpConfig = {
            headers: authHeader
        }
        await apiFetch.post(`${REACT_APP_PS_PORTAL_API_URI}/onboard/${params.practiceId}/cancel`, null, httpConfig)

        setIsSubmitting(false)
        setShowConfirmCancellation(false)
        setShowToastMsg({ visible: true, msg: 'Onboarding cancelled, the cancellation email will be sent shortly' })
        //refresh
        setSubmit(count => count + 1)
    }

    const restartWorkflow = async (setIsSubmitting) => {
        setIsSubmitting(true)

        const authHeader = await getAuthHeader(instance, accounts)
        const httpConfig = {
            headers: authHeader
        }
        await apiFetch.post(`${REACT_APP_PS_PORTAL_API_URI}/onboard/${params.practiceId}/restart`, null, httpConfig)

        setIsSubmitting(false)
        setShowToastMsg({ visible: true, msg: 'Onboarding restarted' })
        setShowRestartOnboardingConfirm(false)
        //refresh
        setSubmit(count => count + 1)
    }

    const regenLimit = 1000 * 60 * 60 * 24; // 1 day
    const onboardingInProgress = () => practice.dataShareAgreementConfirmed && practice.onboardingStatusID < STATUS_COMPLETE
    const workflowSuspended = () => practice.onboardingWorkflowHasError === true && practice.onboardingStatusID < STATUS_INSTALLATION_STARTED
    const regenerateLimitReached = () => practice.onboardingWorkflowCreateTime < (Date.now() - regenLimit);
    const canRegenerateSecret = () => regenerateLimitReached() && practice.onboardingStatusID === STATUS_CLIENT_ID_EMAILED

    return (
        <>
            <ToastContainer className="p-3" position="bottom-center">
                <Toast onClose={() => setShowToastMsg({ visible: false, msg: '' })} show={showToastMsg.visible} delay={3000} autohide>
                    <Toast.Header>
                        <strong className="me-auto">Primary Sense</strong>
                    </Toast.Header>
                    <Toast.Body>{showToastMsg.msg}</Toast.Body>
                </Toast>
            </ToastContainer>
            {practice.id &&
                <>
                    <Formik
                        enableReinitialize={true}
                        initialValues={practice}
                        validationSchema={validationSchema(practice.onboardingStatusID, startOnboarding)}
                        onSubmit={async (values, actions) => {
                            if (startOnboarding)
                            {
                                const authHeader = await getAuthHeader(instance, accounts)
                                await apiFetch.post(`${REACT_APP_PS_PORTAL_API_URI}/onboard/${params.practiceId}/dsaconfirmed`, values, { headers: authHeader })
                                setShowToastMsg({ visible: true, msg: 'DSA confirmed, the welcome email with installation details will be sent to IT provider shortly' })
                                setupAutoStatusUpdate()
                            }
                            else
                            {
                                const authHeader = await getAuthHeader(instance, accounts)
                                await apiFetch.put(`${REACT_APP_PS_PORTAL_API_URI}/onboard/${params.practiceId}/settings`, values, { headers: authHeader })
                                setShowToastMsg({ visible: true, msg: 'Saved' })
                                //refresh
                                setSubmit(count => count + 1)
                            }
                        }}
                    >
                    {({ handleSubmit, handleChange, errors, touched, isSubmitting, isValidating, dirty, resetForm, setFieldError, setFieldTouched, values }) =>
                        {
                            return (
                                <Container>
                                    <Progress status={practice.onboardingStatusID} />
                                    {practice.dataShareAgreementConfirmed && practice.onboardingStatusID < STATUS_CLIENT_ID_EMAILED && !workflowSuspended() &&
                                        <output className="d-block mt-3 mb-3">Onboarding is running automatically. No action is required; this may take several minutes.</output>
                                    }
                                    <Form>
                                        <Row className="mb-3">
                                            <Col xs={9}>
                                                <BForm.Group>
                                                    <BForm.Label htmlFor="practiceNameField" >Practice Name</BForm.Label>
                                                    <Field id="practiceNameField" type="text" name="practiceName" className={'form-control'}
                                                        onChange={(e) => {
                                                            setCustomDirty(d => ({ ...d, practiceName: practice?.practiceName !== e.target.value }))
                                                            handleChange(e)
                                                        }}
                                                    />
                                                    <ErrorMessage name="practiceName" component="div" className="invalid-feedback d-block" />
                                                </BForm.Group>
                                            </Col>
                                            <Col xs={3}>
                                                <BForm.Group>
                                                    <BForm.Label htmlFor="abbreviationField" >Abbreviation</BForm.Label>
                                                    <Field id="abbreviationField" type="text" name="abbreviation" className={'form-control'}
                                                        onChange={(e) => {
                                                            setCustomDirty(d => ({ ...d, abbreviation: practice?.abbreviation !== e.target.value }))
                                                            handleChange(e)
                                                        }}
                                                    />
                                                    <ErrorMessage name="abbreviation" component="div" className="invalid-feedback d-block" />
                                                </BForm.Group>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3">
                                            <Col>
                                                <BForm.Group controlId="practiceSoftwareIdField">
                                                    <BForm.Label htmlFor="practiceSoftwareIdField" >Practice Software</BForm.Label>
                                                    <Field id="practiceSoftwareIdField" name="practiceSoftwareId" as="select" disabled={practice.onboardingStatusID >= 40} className={'form-control form-select'}
                                                        onChange={(e) => {
                                                            setCustomDirty(d => ({ ...d, practiceSoftwareId: practice?.practiceSoftwareId !== e.target.value }))
                                                            handleChange(e)
                                                        }}
                                                    >
                                                        <option value=""></option>
                                                        {
                                                            refData.softwares && refData.softwares.map(s => (
                                                                <option key={s.id} value={s.id}>{s.name}</option>
                                                            ))
                                                        }
                                                    </Field>
                                                    <ErrorMessage name="practiceSoftwareId" component="div" className="invalid-feedback d-block" />
                                                </BForm.Group>
                                            </Col>
                                            <Col>
                                                <BForm.Group controlId="pipQIStatusIDField">
                                                    <BForm.Label htmlFor="pipQIStatusIDField" >PIP QI Status</BForm.Label>
                                                    <Field id="pipQIStatusIDField" name="pipQIStatusID" as="select" className={'form-control form-select'}
                                                        onChange={(e) => {
                                                            setCustomDirty(d => ({ ...d, pipQIStatusID: practice?.pipQIStatusID !== e.target.value }))
                                                            handleChange(e)
                                                        }}
                                                    >
                                                        <option value=""></option>
                                                        {
                                                            refData.pipqiStatus && refData.pipqiStatus.map(s => (
                                                                <option key={s.id} value={s.id}>{s.description}</option>
                                                            ))
                                                        }
                                                    </Field>
                                                    <ErrorMessage name="pipQIStatusID" component="div" className="invalid-feedback d-block" />
                                                </BForm.Group>
                                            </Col>
                                            <Col>
                                                <BForm.Group controlId="postCodeField">
                                                    <BForm.Label htmlFor="postCodeField" >Postcode</BForm.Label>
                                                    <Field id="postCodeField" name="postCode" type="text" className={'form-control'}
                                                        onChange={(e) => {
                                                            setCustomDirty(d => ({ ...d, postCode: practice?.postCode !== e.target.value }))
                                                            handleChange(e)
                                                        }}
                                                    />
                                                    <ErrorMessage name="postCode" component="div" className="invalid-feedback d-block" />
                                                </BForm.Group>
                                            </Col>

                                        </Row>
                                        <Row className="mb-3">
                                            <Col xs={4}>
                                                <BForm.Group>
                                                    <BForm.Label htmlFor="practiceGroupIdField" >Practice Group</BForm.Label>
                                                    <div className="d-flex flex-row align-items-start" >
                                                        <Field id="practiceGroupIdField" name="practiceGroupId" as="select" className={'form-control form-select'}
                                                            onChange={(e) => {
                                                                setCustomDirty(d => ({ ...d, practiceGroupId: practice?.practiceGroupId !== e.target.value }))
                                                                handleChange(e)
                                                            }}
                                                        >
                                                            <option value="0"></option>
                                                            {
                                                                refData.groups && refData.groups.map(s => (
                                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                                ))
                                                            }
                                                        </Field>
                                                    </div>
                                                </BForm.Group>
                                            </Col>
                                            <Col xs={8}>
                                                <OnboardPracticeGroup formLabel="&nbsp;" onClick={fetchRef} />
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col>
                                                <BForm.Group>
                                                    <BForm.Label htmlFor="managerEmailField" >Manager Email</BForm.Label>
                                                    <Field id="managerEmailField" type="text" name="managerEmail" className="form-control"
                                                        onChange={(e) => {
                                                            setCustomDirty(d => ({ ...d, managerEmail: practice?.managerEmail !== e.target.value }))
                                                            handleChange(e)
                                                        }}
                                                    />
                                                    <ErrorMessage name="managerEmail" component="div" className="invalid-feedback d-block" />
                                                </BForm.Group>
                                            </Col>
                                            <Col>
                                                <BForm.Group>
                                                    <BForm.Label htmlFor="itProviderEmailField" >IT Provider Email</BForm.Label>
                                                    <Field id="itProviderEmailField" type="text" name="itProviderEmail" className="form-control"
                                                        onChange={(e) => {
                                                            setCustomDirty(d => ({ ...d, itProviderEmail: practice?.itProviderEmail !== e.target.value }))
                                                            handleChange(e)
                                                        }}
                                                    />
                                                    <ErrorMessage name="itProviderEmail" component="div" className="invalid-feedback d-block" />
                                                </BForm.Group>
                                            </Col>
                                        </Row>

                                        {practice.onboardingStatusID !== STATUS_CANCELLED &&
                                            <Row>
                                                <Col>
                                                    <Button className="ms-3" type="button" variant="primary" disabled={isSubmitting || !(
                                                        customDirty.practiceName ||
                                                        customDirty.abbreviation ||
                                                        customDirty.practiceGroupId ||
                                                        customDirty.practiceSoftwareId ||
                                                        customDirty.pipQIStatusID ||
                                                        customDirty.postCode ||
                                                        customDirty.managerEmail ||
                                                        customDirty.itProviderEmail )}
                                                        onClick={() => {
                                                            setStartOnboarding(false)
                                                            handleSubmit()
                                                    }}>
                                                        Save
                                                    </Button>
                                                </Col>
                                            </Row>
                                        }

                                        {/* END OF FIRST FORM, BEGINNING OF SECOND */}

                                        <Container>
                                            <Card className="mt-4">
                                                <Card.Body>
                                                    <Card.Title>Onboarding</Card.Title>
                                                    <Card.Text>
                                                        <Form>
                                                            {practice.onboardingStatusID > STATUS_TO_BE_ONBOARDED &&
                                                                <Row className="mb-3">
                                                                    <Col>
                                                                        <BForm.Group>
                                                                            <BForm.Label htmlFor="appClientIDField" >App Client ID</BForm.Label>
                                                                            <Field id="appClientIDField" type="text" name="appClientId" className="form-control" disabled={true} />
                                                                        </BForm.Group>
                                                                    </Col>
                                                                </Row>
                                                            }
                                                            <Row className="mb-3">
                                                                <Col>
                                                                    <div className="form-check">
                                                                        <Field id="dataShareCheckbox" type="checkbox" name="dataShareAgreementConfirmed" className="form-check-input" disabled={practice.dataShareAgreementConfirmed}
                                                                            onChange={(e) => {
                                                                                setCustomDirty(d => ({ ...d, onboardingCheck: !(e.target.value === false || practice.dataShareAgreementConfirmed) }))
                                                                                handleChange(e)
                                                                            }}
                                                                        />
                                                                        <BForm.Label htmlFor="dataShareCheckbox" className="form-check-label">Data Share Agreement</BForm.Label>
                                                                        <ErrorMessage name="dataShareAgreementConfirmed" component="div" className="invalid-feedback d-block" />
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                            <Row className="mb-3">
                                                                {!practice.dataShareAgreementConfirmed &&
                                                                    <Col>
                                                                        <Button className="ms-3" type="button" variant="primary" disabled={isSubmitting}
                                                                            onClick={() => {
                                                                                if (!values.dataShareAgreementConfirmed) {
                                                                                    setFieldTouched('dataShareAgreementConfirmed', true, false)
                                                                                    setFieldError('dataShareAgreementConfirmed', 'Please accept data sharing agreement')
                                                                                    return
                                                                                }
                                                                                setStartOnboarding(true)
                                                                                if ((
                                                                                    customDirty.practiceName ||
                                                                                    customDirty.abbreviation ||
                                                                                    customDirty.practiceGroupId ||
                                                                                    customDirty.practiceSoftwareId ||
                                                                                    customDirty.pipQIStatusID ||
                                                                                    customDirty.postCode))
                                                                                {
                                                                                    setShowConfirmSaveDialog(true)
                                                                                }
                                                                                else
                                                                                {
                                                                                    handleSubmit()
                                                                                }
                                                                        }}>
                                                                            Start
                                                                        </Button>
                                                                        {showConfirmSaveDialog &&
                                                                            <ConfirmDialog
                                                                                title="Save Changes?"
                                                                                body='Changes will not be saved. Are you sure you want to start onboarding?'
                                                                                onconfirm={() => { handleSubmit() }}
                                                                                onhide={() => setShowConfirmSaveDialog(false)}
                                                                                overrideOK="Yes"
                                                                                overrideCancel="No"
                                                                            />
                                                                        }
                                                                    </Col>
                                                                }

                                                            </Row>
                                                        </Form>

                                                        {onboardingInProgress() &&
                                                            <>
                                                                <div className="mb-3">
                                                                    {practice.onboardingStatusID === STATUS_INSTALLATION_STARTED &&
                                                                        <Button type="button" variant="primary" onClick={() => setShowConfirmCloseWorkflow(true)}>
                                                                            Force Complete
                                                                        </Button>
                                                                    }
                                                                    {showConfirmCloseWorkflow &&
                                                                        <ConfirmDialog
                                                                            title="Confirm"
                                                                            body="Do you want to complete the onboarding workflow?"
                                                                            onconfirm={closeWorkflow}
                                                                            onhide={() => setShowConfirmCloseWorkflow(false)}
                                                                        />
                                                                    }

                                                                    {practice.onboardingStatusID < STATUS_INSTALLATION_STARTED &&
                                                                        <Button className="ms-3" type="button" variant="warning" onClick={() => setShowConfirmCancellation(true)}>
                                                                            Cancel Onboarding
                                                                        </Button>
                                                                    }

                                                                    {showConfirmCancellation &&
                                                                        <ConfirmDialog
                                                                            title="Confirm"
                                                                            body="Do you want to cancel the onboarding workflow?"
                                                                            onconfirm={cancelWorkflow}
                                                                            onhide={() => setShowConfirmCancellation(false)}
                                                                        />
                                                                    }
                                                                </div>

                                                            </>
                                                        }
                                                    </Card.Text>
                                                </Card.Body>
                                            </Card>

                                            {(workflowSuspended() || canRegenerateSecret()) &&
                                                <Card className="mt-4">
                                                    <Card.Body>
                                                        <Card.Title>Status</Card.Title>
                                                        <Card.Text>
                                                            <p className="lead mt-3">
                                                                <mark>
                                                                    {workflowSuspended()
                                                                        ? 'Something went wrong, the practice onboarding cannot proceed further, please click restart'
                                                                        : 'To regenerate the Client Id and Secret, please click restart'
                                                                    }
                                                                </mark>
                                                            </p>
                                                            <Button className="ms-3" type="button" variant="primary" onClick={() => setShowRestartOnboardingConfirm(true)}>
                                                                Restart
                                                            </Button>

                                                            {showRestartOnboardingConfirm &&
                                                                <ConfirmDialog
                                                                    title="Confirm"
                                                                    body='Restarting will replace the current login details and email new ones. Do you want to continue?'
                                                                    onconfirm={restartWorkflow}
                                                                    onhide={() => setShowRestartOnboardingConfirm(false)}
                                                                />
                                                            }
                                                        </Card.Text>
                                                    </Card.Body>
                                                </Card>
                                            }
                                        </Container>

                                    </Form>
                                </Container>
                            )
                        }}
                    </Formik>

                </>
            }
        </>
    )
}
export default OnboardPracticeDetails
